import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { apiService } from '../services/api';
import type { AppDispatch } from './index';
import type { UserListItem } from '../types/users';

interface UsersState {
  users: UserListItem[];
  userRoles: Record<string, string[]>; // username -> assigned role IDs
  isLoading: boolean;
  error: string | null;
}

const initialState: UsersState = {
  users: [],
  userRoles: {},
  isLoading: false,
  error: null,
};

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setUsers(state, action: PayloadAction<UserListItem[]>) {
      state.users = action.payload;
    },
    setUserRoles(state, action: PayloadAction<Record<string, string[]>>) {
      state.userRoles = action.payload;
    },
    updateUserRoles(state, action: PayloadAction<{ username: string; roles: string[] }>) {
      state.userRoles[action.payload.username] = action.payload.roles;
    },
    addUser(state, action: PayloadAction<UserListItem>) {
      state.users.push(action.payload);
      state.userRoles[action.payload.username] = action.payload.roles ?? [];
    },
    removeUser(state, action: PayloadAction<string>) {
      state.users = state.users.filter((u) => u.username !== action.payload);
      delete state.userRoles[action.payload];
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
  },
});

export const { setUsers, setUserRoles, updateUserRoles, addUser, removeUser, setLoading, setError } =
  usersSlice.actions;

// Fetches the full user list (with email, isUnitAdmin, and roles) in a single API call.
export const fetchUsers =
  () =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    try {
      const res = await apiService.listUsers();
      const items = res.data ?? [];
      dispatch(setUsers(items));

      const rolesMap: Record<string, string[]> = {};
      items.forEach((u) => { rolesMap[u.username] = u.roles ?? []; });
      dispatch(setUserRoles(rolesMap));
    } catch (error: any) {
      dispatch(setError(error?.message ?? 'Failed to load users'));
    } finally {
      dispatch(setLoading(false));
    }
  };

// Re-fetches role assignments for a single user (e.g. after ManageRolesDialog closes).
export const refreshUserRoles =
  (username: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    try {
      const res = await apiService.getUserRoles(username);
      dispatch(updateUserRoles({ username, roles: res.data ?? [] }));
    } catch {
      // Non-fatal — stale roles remain until next full refresh
    }
  };

export default usersSlice.reducer;
